import { makeStyles } from 'tss-react/mui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { ArrowBackIos, ArrowForwardIos, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import SearchIcon from '@mui/icons-material/Search'
import { Badge, Button, IconButton, Modal, Popover, Tooltip } from '@mui/material'
import clsx from 'clsx'
import { find, size, sortBy } from 'lodash'
import ClearInputText from 'assets/icons/clear-input-text.svg'
import IconDoubleArrowLeft from 'assets/icons/double-arrow-left.svg'
import IconDoubleArrowRight from 'assets/icons/double-arrow-right.svg'
import IconDragable from 'assets/icons/dragable.svg'
import ModalContainer from 'components/AgGrid/components/ModalContainer'
import { useKeyPress } from '../../../../hooks/use-keydown'
import styles from './styles.module.scss'

export interface DragableItem {
  label: string
  key: string
  field?: string
  headerName?: string
  selected?: boolean
  icon?: string
  hide?: boolean
  id?: string
}

interface IProps {
  title: string
  items: DragableItem[]
  onClose: () => void
  onApply: (hiddenItems: DragableItem[], visibleItems: DragableItem[]) => void
}

const useStyles = makeStyles()(() => ({
  root: {
    '&': {
      zIndex: `1000 !important`,
    },
  },
  popoverRoot: {
    '& .MuiPaper-root.MuiPopover-paper': {
      borderRadius: '4px !important',
      boxShadow: 'none',
      border: '1px solid #d5d5d5',
    },
  },
}))

export const TwoListDragableModal = ({ title, items, onClose, onApply }: IProps) => {
  const { classes } = useStyles()
  const [hiddenItems, setHiddenItems] = useState<DragableItem[]>([])
  const [visibleItems, setVisibleItems] = useState<DragableItem[]>([])
  const [searchHiddenItem, setSearchHiddenItem] = useState<HTMLElement | null>(null)
  const [searchVisibleItem, setSearchVisibleItem] = useState<HTMLElement | null>(null)
  const [selectOtherItems, setSelectOtherItems] = useState<(string | undefined)[]>([])
  const [searchHiddenItemKey, setSearchHiddenItemKey] = useState('')
  const [searchVisibleItemKey, setSearchVisibleItemKey] = useState('')
  const [itemIndex, setItemIndex] = useState(0)
  const [divFocus, setDivFocus] = useState('hidden')

  const sortItemList = (itemArray: DragableItem[], key = 'label') => sortBy(itemArray, key)

  const hiddenItemList = useMemo(() => {
    const searchKey = searchHiddenItemKey.trim().toLowerCase()
    const baseItems = hiddenItems
    if (!searchKey) {
      return sortItemList(baseItems)
    }
    const filtered = baseItems.filter((item) => item.label.toLowerCase().includes(searchKey))
    return sortItemList(filtered)
  }, [hiddenItems, searchHiddenItemKey])

  const visibleItemList = useMemo(() => {
    const searchKey = searchVisibleItemKey.trim().toLowerCase()
    const baseItems = visibleItems
    if (!searchKey) {
      return baseItems
    }
    return baseItems.filter((item) => item.label.toLowerCase().includes(searchKey))
  }, [visibleItems, searchVisibleItemKey])

  useEffect(() => {
    const nextHidden: DragableItem[] = []
    const nextVisible: DragableItem[] = []

    for (const item of items) {
      const nextItem: DragableItem = { ...item, selected: false }
      if (item.hide) {
        nextHidden.push(nextItem)
      } else {
        nextVisible.push(nextItem)
      }
    }

    setHiddenItems(nextHidden)
    setVisibleItems(nextVisible)
    // We intentionally only want to derive initial state from items when the
    // modal mounts or when items identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  // useKeyPress('ArrowUp', () => {
  //   setItemIndex((prevIndex) => {
  //     const nextIndex = prevIndex - 1
  //     if (nextIndex < 0) {
  //       return 0
  //     }
  //     return nextIndex
  //   })
  // })
  useKeyPress('ArrowDown', () => {
    setItemIndex((prevIndex) => {
      const currentList = divFocus === 'hidden' ? hiddenItemList : visibleItemList
      if (currentList.length === 0) return 0
      const nextIndex = prevIndex + 1
      return nextIndex === currentList.length ? 0 : nextIndex
    })
  })

  const dropItemInArray = (arr: any[], sourceIndex: number, destinationIndex: number): any[] => {
    const item = arr[sourceIndex]
    arr.splice(sourceIndex, 1)
    arr.splice(destinationIndex, 0, item)
    return [...arr]
  }

  const isHoverEffect = (index: any, section: any) => {
    return itemIndex === index && section === divFocus
  }

  const closeSearchPopup = (isHidden: boolean) => {
    setItemIndex(0)
    isHidden ? setSearchHiddenItem(null) : setSearchVisibleItem(null)
  }

  const getSelectedItemCount = (isHidden: boolean) => {
    const items = isHidden ? hiddenItems : visibleItems
    const filterSelectItems = items.filter((item) => item.selected)
    return filterSelectItems.length
  }

  const getSelectedOtherItem = (items: any, id: any) => {
    const filterSelectItems = items.filter((item: any) => item.selected && item.id !== id)
    return filterSelectItems.map((item: any) => item.id)
  }

  const renderCardList = (itemList: DragableItem[], isVisibleArr: boolean) => {
    const selectItemCount = getSelectedItemCount(!isVisibleArr)

    const toggleSelected = useCallback(
      (item: DragableItem) => {
        const updatedItem = { ...item, selected: !item.selected }
        const itemList = isVisibleArr ? visibleItems : hiddenItems
        const setItemList = isVisibleArr ? setVisibleItems : setHiddenItems
        setItemList(itemList.map((i) => (i.id === item.id ? updatedItem : i)))
      },
      [isVisibleArr, visibleItems, hiddenItems]
    )

    return (
      <Droppable droppableId={isVisibleArr ? 'visible' : 'hidden'}>
        {(providedList) => (
          <div
            className={clsx(styles.items)}
            {...providedList.droppableProps}
            ref={providedList.innerRef}
          >
            {itemList.map((item, index) => (
              <Draggable key={item.label} draggableId={item.label} index={index}>
                {(provided, snapshot) => {
                  const visibility = isVisibleArr ? 'visible' : 'hidden'
                  const hoverSelectedClass = isHoverEffect(index, visibility)
                    ? styles.selected
                    : null
                  const isDraggingMultiple =
                    snapshot.isDragging && selectItemCount > 1 && item.selected
                  const badgeCount = isDraggingMultiple ? selectItemCount : 0
                  const handleRowClick = () => {
                    toggleSelected(item)
                    setItemIndex(index)
                    setDivFocus(isVisibleArr ? 'visible' : 'hidden')
                  }
                  return (
                    <div
                      className={clsx(
                        styles.item,
                        item.selected ? styles.selected : null,
                        selectOtherItems.includes(item.id) ? styles.hideDiv : null,
                        hoverSelectedClass
                      )}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={handleRowClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleRowClick()
                        }
                      }}
                    >
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation()
                          toggleSelected(item)
                        }}
                        aria-label={item.selected ? 'Deselect item' : 'Select item'}
                      >
                        {item.selected ? (
                          <CheckBox className={styles.selectedCheckbox} />
                        ) : (
                          <CheckBoxOutlineBlank className="color-gray" />
                        )}
                      </IconButton>
                      {!!item.icon && <img src={item.icon} alt={`${item.label} icon`} />}
                      <span className="flex-1">{item.label}</span>
                      <div>
                        <Badge badgeContent={badgeCount} color="primary">
                          <img src={IconDragable} alt="dragable icon" />
                        </Badge>
                      </div>
                    </div>
                  )
                }}
              </Draggable>
            ))}
            {providedList.placeholder}
          </div>
        )}
      </Droppable>
    )
  }

  const unselectAll = (arr: DragableItem[]): DragableItem[] => {
    return arr.map((item) => ({ ...item, selected: false }))
  }

  const addAtIndex = (items: DragableItem[], item: DragableItem, index: number) => {
    const newItems = [...items]
    newItems.splice(index, 0, item)
    return newItems
  }

  const addMultiAtIndex = (items: DragableItem[], selectedItems: DragableItem[], index: number) => {
    const newItems = [...items]
    newItems.splice(index, 0, ...selectedItems)
    return newItems
  }

  const setSearchItemKey = (isHidden: boolean, value: string) => {
    isHidden ? setSearchHiddenItemKey(value) : setSearchVisibleItemKey(value)
  }
  const renderSearchBtn = (isHidden: boolean) => {
    return (
      <div>
        <Tooltip title="Search" placement="top" arrow>
          <IconButton
            className={styles.btnRight}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              isHidden
                ? setSearchHiddenItem(event.currentTarget)
                : setSearchVisibleItem(event.currentTarget)
            }}
          >
            <SearchIcon className={clsx(styles.colorSearchBlueIcon)} />
          </IconButton>
        </Tooltip>
        <Popover
          className={classes.popoverRoot}
          open={Boolean(isHidden ? searchHiddenItem : searchVisibleItem)}
          anchorEl={isHidden ? searchHiddenItem : searchVisibleItem}
          onClose={() => closeSearchPopup(isHidden)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <div className={clsx('d-flex align-items-center', styles.sectionSearchItem)}>
            <IconButton className={styles.btnSearch}>
              <SearchIcon className={clsx(styles.colorSearchBlueIcon)} />
            </IconButton>
            <input
              autoFocus
              value={isHidden ? searchHiddenItemKey : searchVisibleItemKey}
              className="flex-1"
              type="text"
              placeholder="Search"
              onChange={(event) => {
                setItemIndex(0)
                setSearchItemKey(isHidden, event.target.value)
              }}
            />
            {isHidden && searchHiddenItemKey !== '' && (
              <IconButton onClick={() => setSearchHiddenItemKey('')} className={styles.btnCross}>
                <img
                  src={ClearInputText}
                  className={clsx(styles.colorSearchIcon)}
                  alt="clear input"
                />
              </IconButton>
            )}
            {!isHidden && searchVisibleItemKey !== '' && (
              <IconButton onClick={() => setSearchVisibleItemKey('')} className={styles.btnCross}>
                <img
                  src={ClearInputText}
                  className={clsx(styles.colorSearchIcon)}
                  alt="clear input"
                />
              </IconButton>
            )}
          </div>
        </Popover>
      </div>
    )
  }
  return (
    <Modal
      disableRestoreFocus
      open={true}
      disablePortal={true}
      aria-labelledby="customize-columns-title"
      onClose={onClose}
    >
      <ModalContainer
        className={clsx(styles.container)}
        header={title}
        classNameHeader={clsx(styles.header)}
        onClose={onClose}
      >
        <DragDropContext
          onDragStart={(dropResult) => {
            const { source, draggableId } = dropResult
            const items = source.droppableId === 'visible' ? visibleItems : hiddenItems
            const item = find(items, (item) => item.label === draggableId)
            if (item?.selected) {
              const otherItem = getSelectedOtherItem(items, item.id)
              setSelectOtherItems([...otherItem])
            }
          }}
          onDragEnd={(dropResult) => {
            const { destination, source, draggableId } = dropResult
            setSelectOtherItems([])
            if (source.index >= 0 && destination && destination.index >= 0) {
              const destIdx = destination.index
              if (destination.droppableId !== source.droppableId) {
                if (destination.droppableId === 'visible') {
                  const item = find(hiddenItems, (item) => item.label === draggableId)
                  if (item) {
                    const selectedItemCount = getSelectedItemCount(true)
                    if (selectedItemCount > 1 && item.selected) {
                      const selectedItems = [...hiddenItems].filter((item) => item.selected)
                      setVisibleItems((items) => addMultiAtIndex(items, selectedItems, destIdx))
                      setHiddenItems((items) => items.filter((item) => !item.selected))
                    } else {
                      setVisibleItems((items) => addAtIndex(items, item, destIdx))
                      setHiddenItems((items) => items.filter((item) => item.label !== draggableId))
                    }
                  }
                } else if (destination.droppableId === 'hidden') {
                  const item = find(visibleItems, (item) => item.label === draggableId)
                  if (item) {
                    const selectedItemCount = getSelectedItemCount(false)
                    if (selectedItemCount > 1 && item.selected) {
                      const selectedItems = [...visibleItems].filter((item) => item.selected)
                      setVisibleItems((items) => items.filter((item) => !item.selected))
                      setHiddenItems((items) => addMultiAtIndex(items, selectedItems, destIdx))
                    } else {
                      setHiddenItems((items) => addAtIndex(items, item, destIdx))
                      setVisibleItems((items) => items.filter((item) => item.label !== draggableId))
                    }
                  }
                }
              } else if (destination.droppableId === 'visible') {
                setVisibleItems(dropItemInArray(visibleItems, source.index, destination.index))
              } else {
                setHiddenItems(dropItemInArray(hiddenItems, source.index, destination.index))
              }
            }
          }}
        >
          <main className={clsx(styles.bodyContent)}>
            <div className="d-flex justify-content-between">
              <div className={clsx(styles.card, 'flex-1')}>
                <div className={clsx(styles.cardHeader)}>
                  Hidden
                  {renderSearchBtn(true)}
                </div>
                {renderCardList(hiddenItemList, false)}
              </div>
              <div className={clsx(styles.buttons)}>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={hiddenItems.filter((item) => item.selected).length === 0}
                  onClick={() => {
                    setVisibleItems(
                      unselectAll([...visibleItems, ...hiddenItems.filter((item) => item.selected)])
                    )
                    setHiddenItems([...hiddenItems.filter((item) => !item.selected)])
                  }}
                  aria-label="Move selected to visible"
                >
                  <ArrowForwardIos className={styles.buttonIcon} aria-hidden="true" />
                  <span className={styles.mobileButtonLabel}>Move selected to visible</span>
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={!size(hiddenItems)}
                  onClick={() => {
                    setVisibleItems(unselectAll([...visibleItems, ...hiddenItems]))
                    setHiddenItems([])
                  }}
                  aria-label="Move all to visible"
                >
                  <img
                    src={IconDoubleArrowRight}
                    alt="double arrow right"
                    className={styles.buttonIcon}
                    aria-hidden="true"
                  />
                  <span className={styles.mobileButtonLabel}>Move all to visible</span>
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={!size(visibleItems)}
                  onClick={() => {
                    setHiddenItems(unselectAll([...hiddenItems, ...visibleItems]))
                    setVisibleItems([])
                  }}
                  aria-label="Move all to hidden"
                >
                  <img
                    src={IconDoubleArrowLeft}
                    alt="double arrow left"
                    className={styles.buttonIcon}
                    aria-hidden="true"
                  />
                  <span className={styles.mobileButtonLabel}>Move all to hidden</span>
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={visibleItems.filter((item) => item.selected).length === 0}
                  onClick={() => {
                    setHiddenItems(
                      unselectAll([...hiddenItems, ...visibleItems.filter((item) => item.selected)])
                    )
                    setVisibleItems([...visibleItems.filter((item) => !item.selected)])
                  }}
                  aria-label="Move selected to hidden"
                >
                  <ArrowBackIos className={styles.buttonIcon} aria-hidden="true" />
                  <span className={styles.mobileButtonLabel}>Move selected to hidden</span>
                </Button>
              </div>
              <div className={clsx(styles.card, 'flex-1')}>
                <div className={clsx(styles.cardHeader)}>
                  Visible
                  {renderSearchBtn(false)}
                </div>
                {renderCardList(visibleItemList, true)}
              </div>
            </div>
            <div className={clsx('d-flex justify-content-end', styles.bottomBtns)}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  onApply(hiddenItems, visibleItems)
                  onClose()
                }}
              >
                APPLY
              </Button>
              <Button
                className={clsx('CustomButtonPurple')}
                variant="outlined"
                onClick={() => onClose()}
              >
                CANCEL
              </Button>
            </div>
          </main>
        </DragDropContext>
      </ModalContainer>
    </Modal>
  )
}
export default TwoListDragableModal
